import React from "react";
import Modal from "react-modal";
import * as SliderPrimitive from "@radix-ui/react-slider";
import { Button } from "@/components/Button/Button";
import { KPIThreshold } from "../../api/fetchMemberDashboard/types";
import { useKPIThresholdModal } from "../../hooks/useKPIThresholdModal";
import { DashboardKPI } from "../../types";
import {
  ThresholdBar,
  ThresholdBarLabelAlignment,
  ThresholdScale,
} from "../../utils/thresholdBar";
import { ThresholdZone } from "../../utils/thresholdZones";

interface KPIThresholdModalProps {
  kpi: DashboardKPI;
  onChange: (threshold: KPIThreshold | null) => void;
  onClose: () => void;
}

interface ZoneNameProps {
  zone: ThresholdZone;
}

interface BoundaryValueProps {
  wording: string;
  value: string;
}

interface ThresholdSliderProps {
  bar: ThresholdBar;
  scale: ThresholdScale;
  boundaries: number[];
  handleLabels: string[];
  onBoundariesChange: (values: number[]) => void;
}

const ZONE_COLOR_CLASSES: Record<ThresholdZone, string> = {
  "On target": "bg-green-500",
  Watch: "bg-yellow-400",
  "Off target": "bg-red-500",
};

const LABEL_ALIGNMENT_CLASSES: Record<ThresholdBarLabelAlignment, string> = {
  center: "-translate-x-1/2",
  end: "-translate-x-full pr-1",
  start: "pl-1",
};

const ZoneName: React.FC<ZoneNameProps> = ({ zone }) => (
  <span className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
    <span
      aria-hidden="true"
      className={`h-2 w-2 shrink-0 rounded-full ${ZONE_COLOR_CLASSES[zone]}`}
    />
    {zone}
  </span>
);

const BoundaryValue: React.FC<BoundaryValueProps> = ({ wording, value }) => (
  <span className="flex items-center gap-2">
    <span className="text-gray-500 dark:text-gray-400">{wording}</span>
    <span className="text-gray-900 dark:text-white">{value}</span>
  </span>
);

/**
 * The track and the mark rows are inset by half a handle (mx-2), which is
 * where Radix keeps the handle centres, so the colours line up under them.
 */
const ThresholdSlider: React.FC<ThresholdSliderProps> = ({
  bar,
  scale,
  boundaries,
  handleLabels,
  onBoundariesChange,
}) => (
  <div className="mt-5" data-testid="threshold-bar">
    {bar.latestValue && (
      <div
        aria-hidden="true"
        className="relative mx-2 mb-1 h-4 text-xs font-medium text-gray-900 dark:text-white"
      >
        <span
          className="absolute -translate-x-1/2 whitespace-nowrap"
          data-testid="threshold-bar-latest-value"
          style={{ left: `${bar.latestValue.positionPercent}%` }}
        >
          {bar.latestValue.label}
        </span>
      </div>
    )}
    <SliderPrimitive.Root
      className="relative flex h-5 w-full touch-none select-none items-center"
      max={scale.max}
      min={scale.min}
      minStepsBetweenThumbs={1}
      onValueChange={onBoundariesChange}
      step={scale.step}
      value={boundaries}
    >
      <SliderPrimitive.Track
        className="relative mx-2 h-2 grow rounded-full"
        style={{ backgroundImage: bar.gradient }}
      >
        {bar.latestValue && (
          <span
            aria-hidden="true"
            className="absolute top-1/2 h-4 w-0.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gray-900 dark:bg-white"
            style={{ left: `${bar.latestValue.positionPercent}%` }}
          />
        )}
      </SliderPrimitive.Track>
      {bar.boundaries.map((boundary, index) => (
        <SliderPrimitive.Thumb
          aria-label={handleLabels[index]}
          aria-valuetext={boundary.label}
          className="relative block h-4 w-4 cursor-grab rounded-full border-2 border-gray-900 bg-white shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary active:cursor-grabbing dark:border-white dark:bg-slate-800"
          key={handleLabels[index]}
        >
          <span
            aria-hidden="true"
            className={`absolute left-1/2 top-full mt-1.5 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400 ${LABEL_ALIGNMENT_CLASSES[boundary.alignment]}`}
          >
            {boundary.label}
          </span>
        </SliderPrimitive.Thumb>
      ))}
    </SliderPrimitive.Root>
    <div className="h-5" />
  </div>
);

/**
 * Rendered only while open, so every opening starts from the saved values.
 * Styled through classes rather than react-modal's inline `style` so the dark
 * palette applies.
 */
const KPIThresholdModal: React.FC<KPIThresholdModalProps> = ({
  kpi,
  onChange,
  onClose,
}) => {
  const {
    scale,
    boundaries,
    handleBoundariesChange,
    handleLabels,
    boundaryWording,
    onTargetValueLabel,
    offTargetValueLabel,
    watchRange,
    bar,
    latestValueLabel,
    latestValueZone,
    canSave,
    canClear,
    isBusy,
    save,
    clear,
  } = useKPIThresholdModal(kpi, onChange);

  return (
    <Modal
      ariaHideApp={false}
      className="w-[500px] max-w-[calc(100vw-2rem)] rounded-lg border border-gray-200 bg-white p-6 outline-none dark:border-slate-700 dark:bg-slate-800"
      contentLabel={`${kpi.name} thresholds`}
      isOpen
      onRequestClose={onClose}
      overlayClassName="fixed inset-0 z-[9999] flex items-center justify-center bg-black/30"
    >
      <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
        {kpi.name} thresholds
      </h3>
      <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">
        Drag the handles to set the ranges for this company.{" "}
        {kpi.direction === "lower_is_better"
          ? "Lower is better for this KPI."
          : "Higher is better for this KPI."}
      </p>

      <ThresholdSlider
        bar={bar}
        boundaries={boundaries}
        handleLabels={handleLabels}
        onBoundariesChange={handleBoundariesChange}
        scale={scale}
      />

      <ul
        className="mt-3 space-y-3 rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-900/50"
        data-testid="threshold-zones"
      >
        <li className="flex items-center justify-between gap-3">
          <ZoneName zone="On target" />
          <BoundaryValue
            value={onTargetValueLabel}
            wording={boundaryWording.onTarget}
          />
        </li>
        <li className="flex items-center justify-between gap-3">
          <ZoneName zone="Watch" />
          <span className="text-gray-900 dark:text-white">{watchRange}</span>
        </li>
        <li className="flex items-center justify-between gap-3">
          <ZoneName zone="Off target" />
          <BoundaryValue
            value={offTargetValueLabel}
            wording={boundaryWording.offTarget}
          />
        </li>
      </ul>

      {latestValueLabel && (
        <p
          className="mt-3 text-sm text-gray-500 dark:text-gray-400"
          data-testid="latest-value"
        >
          Latest value:{" "}
          <span className="text-gray-900 dark:text-white">
            {latestValueLabel}
          </span>
          {latestValueZone && ` → ${latestValueZone}`}
        </p>
      )}

      <div className="mt-6 flex justify-end gap-3">
        <Button className="rounded-md" onClick={onClose} variant="outline">
          Cancel
        </Button>
        {kpi.threshold && (
          <Button
            className="rounded-md"
            disabled={!canClear}
            onClick={clear}
            variant="outline"
          >
            Clear thresholds
          </Button>
        )}
        <Button
          className="rounded-md"
          disabled={!canSave}
          onClick={save}
          variant="default"
        >
          {isBusy ? "Saving..." : "Save"}
        </Button>
      </div>
    </Modal>
  );
};

export default KPIThresholdModal;
